import { FC, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue } from "recoil";

import {
  ErrorDisplayUnsavedItems,
  RideStationDragOverlay,
} from "@ryde/controls";
import {
  useConfirmDirtyField,
  useCrudEditContext,
  useRideFormCleanup,
} from "@ryde/redux";
import {
  DRAWER_MOTION_RIDE_ENTER_DURATION_S,
  ICrudDrawerProps,
  IDrawersCommonProps,
  IInjectedDrawerProps,
  IRideDto,
  isPendingDuplicateSourceDto,
  isStaleEditFormDto,
  loadersEditFormDataSF,
  RideCrudModeKey,
  rideDrawerSignals,
  RideEntity,
  rideHasUnsavedStationChangeAtom,
  ridePostSaveTransitionAtom,
  rideSaveInProgressAtom,
  RIGHTBAR_RIDES_WIDTH,
  simpleResultFalse,
  useIsMobile,
} from "@ryde/shared";

import { BaseDrawer, DrawerFormContentGate } from "../../drawer";
import { useRideDrawerResize } from "./hooks";
import { RideFooter } from "./ride-footer";
import { RideHeader } from "./ride-header";
import { RideMap } from "./ride-map";
import { RideTabsWrapper } from "./ride-tabs-wrapper";
import { RideDrawerWrapper } from "./ride.styled";

export const RideEditContentDrawer: FC<
  IDrawersCommonProps & IInjectedDrawerProps & ICrudDrawerProps
> = ({ resolveFn, onCloseDrawer, drawerNumber, id }) => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const isFormBusy = useRecoilValue(
    loadersEditFormDataSF(RideCrudModeKey.uiUnit),
  );
  const { cleanupRideFormStateAtoms } = useRideFormCleanup();
  const rideHasUnsavedStationChange = useRecoilValue(
    rideHasUnsavedStationChangeAtom,
  );
  const isRideSaveInProgress = useRecoilValue(rideSaveInProgressAtom);
  const [isRidePostSaveTransition, setRidePostSaveTransition] = useRecoilState(
    ridePostSaveTransitionAtom,
  );
  const {
    idCopyFrom,
    data: { dto: rideDto },
  } = useCrudEditContext<IRideDto>();

  const {
    formState: { isDirty },
  } = useFormContext<Partial<RideEntity>>();

  // Keep refs in sync so the close confirm path never reads a stale render snapshot
  // after an await (slow save / soft-validation dialogs / network).
  const isDirtyRef = useRef(isDirty);
  const rideHasUnsavedStationChangeRef = useRef(rideHasUnsavedStationChange);
  const isRideSaveInProgressRef = useRef(isRideSaveInProgress);
  const isRidePostSaveTransitionRef = useRef(isRidePostSaveTransition);

  isDirtyRef.current = isDirty;
  rideHasUnsavedStationChangeRef.current = rideHasUnsavedStationChange;
  isRideSaveInProgressRef.current = isRideSaveInProgress;
  isRidePostSaveTransitionRef.current = isRidePostSaveTransition;

  // While a save is in flight (or during the post-save drawer switch, before
  // the reopened drawer's form settles clean) the ride is being persisted, so
  // closing loses no changes — skip the unsaved-changes prompt.
  const getIsRideDirty = useEvent((): boolean => {
    const isSaveInFlight =
      isRideSaveInProgressRef.current || rideDrawerSignals.isSaveInProgress;
    const isPostSave = isRidePostSaveTransitionRef.current;

    return (
      (isDirtyRef.current || rideHasUnsavedStationChangeRef.current) &&
      !isSaveInFlight &&
      !isPostSave &&
      !rideDrawerSignals.suppressUnsavedPromptUntilUnmount
    );
  });

  // Post-save transition is over once the form is clean again — re-enable the
  // unsaved-changes guard for any subsequent genuine edits. Station flag is
  // cleared on successful save; do not gate this on it or transition can stick.
  useEffect(() => {
    if (isRidePostSaveTransition && !isDirty) {
      setRidePostSaveTransition(false);
    }
  }, [isRidePostSaveTransition, isDirty, setRidePostSaveTransition]);

  // Ride-to-ride route changes remount this drawer (`key={pathname}`). Reset
  // ride flags when Recoil still holds another record so Link navigation
  // cannot leak dirty/unsaved state. Keep editFormDataAF: clearing it while
  // `id` is set would make useCrudEdit refetch.
  useEffect(() => {
    if (id && rideDto?.id !== undefined && rideDto.id !== id) {
      cleanupRideFormStateAtoms();
    }
  }, [cleanupRideFormStateAtoms, id, rideDto?.id]);

  // Mid-save close suppress must outlive form-atom cleanup (which runs in
  // performClose before React unmounts). Clear only when the drawer is gone.
  useEffect(() => {
    return () => {
      rideDrawerSignals.suppressUnsavedPromptUntilUnmount = false;
    };
  }, []);

  const { applyIfConfirmed } = useConfirmDirtyField({
    customContent: <ErrorDisplayUnsavedItems />,
  });

  const performClose = useEvent((): void => {
    // Prefer the sync signal so we still mark close-during-save if Recoil has
    // not re-rendered yet after save started.
    const isSaveInFlight =
      rideDrawerSignals.isSaveInProgress || isRideSaveInProgressRef.current;

    if (isSaveInFlight) {
      rideDrawerSignals.isCloseRequestedDuringSave = true;
      // Keep the unsaved-changes guard suppressed until unmount: after save's
      // `finally` clears `isSaveInProgress`, the drawer can still be mounted
      // for a frame with isDirty=true and would otherwise re-prompt.
      rideDrawerSignals.suppressUnsavedPromptUntilUnmount = true;
    }

    resolveFn?.(simpleResultFalse);
    // Flags only — do not clear editFormDataAF while `id` is still set, or
    // useCrudEdit would see !dto && id and refetch on close.
    cleanupRideFormStateAtoms();
    // onCloseDrawer already invokes onCloseCustomAction via DrawersContainer.
    onCloseDrawer();
  });

  // Escape is handled by DrawerBackdrop — do not register a second Escape
  // listener here or one keypress would open/confirm the unsaved dialog twice.
  const onCloseClickConfirmed = applyIfConfirmed({
    isDirty: getIsRideDirty,
  })(performClose);

  const {
    gridStyle,
    drawerWidthStyle,
    isMapVisible,
    onFormHandleMouseDown,
    onFormHandleKeyDown,
    onMapHandleMouseDown,
    onMapHandleKeyDown,
  } = useRideDrawerResize({ drawerNumber });

  const isRideDtoStale = isStaleEditFormDto({
    requestedId: id,
    dtoId: rideDto?.id,
    hasDto: Boolean(rideDto),
  });
  const isDuplicateSourcePending = isPendingDuplicateSourceDto({
    idCopyFrom,
    dtoId: rideDto?.id,
  });
  const isRideDataLoading =
    isFormBusy || (Boolean(id) && isRideDtoStale) || isDuplicateSourcePending;

  return (
    <BaseDrawer
      drawerNumber={drawerNumber}
      onCloseDrawer={onCloseClickConfirmed}
      customWidth={isMobile ? RIGHTBAR_RIDES_WIDTH : drawerWidthStyle}
      enterDurationSeconds={DRAWER_MOTION_RIDE_ENTER_DURATION_S}
    >
      <DrawerFormContentGate
        isLoading={isRideDataLoading}
        entityTitle={t("ride.single")}
      >
        <RideDrawerWrapper style={gridStyle}>
          <RideHeader closeClick={() => onCloseClickConfirmed()} />
          {!isMobile && (
            // eslint-disable-next-line jsx-a11y-x/no-noninteractive-element-interactions -- intentional: keyboard-operable resize handle
            <div
              className="RideDrawer-LeftHandle"
              onMouseDown={onFormHandleMouseDown}
              onKeyDown={onFormHandleKeyDown}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize form section"
              // eslint-disable-next-line jsx-a11y-x/no-noninteractive-tabindex -- intentional: focusable resize handle operated via onKeyDown
              tabIndex={0}
            />
          )}
          <RideTabsWrapper />
          <RideFooter />
          {!isMobile && (
            // eslint-disable-next-line jsx-a11y-x/no-noninteractive-element-interactions -- intentional: keyboard-operable resize handle
            <div
              className="RideDrawer-MiddleHandle"
              onMouseDown={onMapHandleMouseDown}
              onKeyDown={onMapHandleKeyDown}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize map section"
              // eslint-disable-next-line jsx-a11y-x/no-noninteractive-tabindex -- intentional: focusable resize handle operated via onKeyDown
              tabIndex={0}
            />
          )}
          {!isMobile && rideDto && isMapVisible && <RideMap />}
        </RideDrawerWrapper>
      </DrawerFormContentGate>
      <RideStationDragOverlay />
    </BaseDrawer>
  );
};
